import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import ReceiptUploader, { ReceiptBadge, ReceiptGallery } from "../components/ReceiptUploader";
import AddableSelect from "../components/AddableSelect";
import { api, fmtUSD, fmtNum, todayISO } from "../lib/api";
import { useYear } from "../context/YearContext";
import { Plus, Wrench, Trash2, Pencil, Tractor, ChevronDown, ChevronRight, Truck, Layers, CalendarClock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EQUIPMENT_TYPES = ["Tractor", "Combine", "Truck", "Implement", "Sprayer", "Planter", "Other"];

const blankEq = {
  name: "", type: "Tractor", purchase_date: todayISO(), purchase_cost: "",
  serial_number: "", notes: "", tax_treatment: "expense", depreciation_method: "straight_line", useful_life_years: 7, receipts: [],
};

const MACRS_CLASSES = [
  { life: 3, label: "3-year (special tools, racehorses)" },
  { life: 5, label: "5-year (trucks, cars, computers)" },
  { life: 7, label: "7-year (tractors, combines, most farm equip.)" },
  { life: 10, label: "10-year (tree & vine orchards)" },
  { life: 15, label: "15-year (land improvements, drainage)" },
  { life: 20, label: "20-year (farm buildings)" },
];

const blankRepair = {
  equipment_id: "", date: todayISO(), description: "", cost: "",
  vendor: "", field_id: "", notes: "", receipts: [],
};

const blankMaint = {
  equipment_id: "", title: "", interval_days: 90, last_done_date: todayISO(), notes: "",
};

const typeIcon = (t) => {
  if (t === "Truck") return Truck;
  if (t === "Implement" || t === "Planter" || t === "Sprayer") return Layers;
  return Tractor;
};

export default function Equipment() {
  const { year, refreshYears } = useYear();
  const [equipment, setEquipment] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [fields, setFields] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [eqOpen, setEqOpen] = useState(false);
  const [eqEditing, setEqEditing] = useState(null);
  const [eqForm, setEqForm] = useState(blankEq);
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairEditing, setRepairEditing] = useState(null);
  const [repairForm, setRepairForm] = useState(blankRepair);
  const [maintOpen, setMaintOpen] = useState(false);
  const [maintEditing, setMaintEditing] = useState(null);
  const [maintForm, setMaintForm] = useState(blankMaint);
  const [galleryReceipts, setGalleryReceipts] = useState(null);

  const load = async () => {
    const [eq, rp, mn, fl] = await Promise.all([
      api.get("/equipment"),
      api.get("/repairs"),
      api.get("/maintenance"),
      api.get(`/fields?year=${year}`),
    ]);
    setEquipment(eq.data);
    setRepairs(rp.data);
    setMaintenance(mn.data);
    setFields(fl.data);
  };

  useEffect(() => { load(); }, [year]);

  const repairsFor = (eqId) => repairs.filter((r) => r.equipment_id === eqId);
  const maintFor = (eqId) => maintenance.filter((m) => m.equipment_id === eqId);
  const totalRepairCost = (eqId) => repairsFor(eqId).reduce((s, r) => s + (r.cost || 0), 0);

  const onSubmitEq = async (e) => {
    e.preventDefault();
    const yearFromDate = new Date(eqForm.purchase_date).getFullYear();
    const payload = {
      ...eqForm,
      purchase_cost: parseFloat(eqForm.purchase_cost) || 0,
      useful_life_years: parseInt(eqForm.useful_life_years) || 7,
      year: yearFromDate || year,
    };
    try {
      if (eqEditing) {
        await api.put(`/equipment/${eqEditing.id}`, payload);
        toast.success("Equipment updated");
      } else {
        await api.post("/equipment", payload);
        toast.success("Equipment added (expense auto-logged)");
      }
      setEqOpen(false);
      setEqEditing(null);
      setEqForm(blankEq);
      refreshYears();
      load();
    } catch {
      toast.error("Could not save equipment");
    }
  };

  const onEditEq = (eq) => {
    setEqEditing(eq);
    setEqForm({
      name: eq.name,
      type: eq.type,
      purchase_date: eq.purchase_date,
      purchase_cost: eq.purchase_cost,
      serial_number: eq.serial_number || "",
      notes: eq.notes || "",
      tax_treatment: eq.tax_treatment || "expense",
      depreciation_method: eq.depreciation_method || "straight_line",
      useful_life_years: eq.useful_life_years || 7,
      receipts: eq.receipts || [],
    });
    setEqOpen(true);
  };

  const onDeleteEq = async (id) => {
    if (!window.confirm("Delete this equipment? All linked repairs and auto-expenses will also be removed.")) return;
    await api.delete(`/equipment/${id}`);
    toast.success("Equipment deleted");
    load();
  };

  const openRepairModal = (eq, existingRepair = null) => {
    if (existingRepair) {
      setRepairEditing(existingRepair);
      setRepairForm({
        equipment_id: existingRepair.equipment_id,
        date: existingRepair.date,
        description: existingRepair.description,
        cost: existingRepair.cost,
        vendor: existingRepair.vendor || "",
        field_id: existingRepair.field_id || "",
        notes: existingRepair.notes || "",
        receipts: existingRepair.receipts || [],
      });
    } else {
      setRepairEditing(null);
      setRepairForm({ ...blankRepair, equipment_id: eq.id, date: todayISO() });
    }
    setRepairOpen(true);
  };

  const onSubmitRepair = async (e) => {
    e.preventDefault();
    const yearFromDate = new Date(repairForm.date).getFullYear();
    const payload = {
      ...repairForm,
      cost: parseFloat(repairForm.cost) || 0,
      year: yearFromDate || year,
    };
    try {
      if (repairEditing) {
        await api.put(`/repairs/${repairEditing.id}`, payload);
        toast.success("Repair updated");
      } else {
        await api.post("/repairs", payload);
        toast.success("Repair logged (expense auto-created)");
      }
      setRepairOpen(false);
      setRepairEditing(null);
      setRepairForm(blankRepair);
      refreshYears();
      load();
    } catch {
      toast.error("Could not save repair");
    }
  };

  const onDeleteRepair = async (id) => {
    if (!window.confirm("Delete this repair? The linked expense will also be removed.")) return;
    await api.delete(`/repairs/${id}`);
    toast.success("Repair deleted");
    load();
  };

  const openMaintModal = (eq, existing = null) => {
    if (existing) {
      setMaintEditing(existing);
      setMaintForm({
        equipment_id: existing.equipment_id,
        title: existing.title,
        interval_days: existing.interval_days,
        last_done_date: existing.last_done_date,
        notes: existing.notes || "",
      });
    } else {
      setMaintEditing(null);
      setMaintForm({ ...blankMaint, equipment_id: eq.id, last_done_date: todayISO() });
    }
    setMaintOpen(true);
  };

  const onSubmitMaint = async (e) => {
    e.preventDefault();
    const payload = { ...maintForm, interval_days: parseInt(maintForm.interval_days) || 0 };
    try {
      if (maintEditing) {
        await api.put(`/maintenance/${maintEditing.id}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/maintenance", payload);
        toast.success("Maintenance schedule added");
      }
      setMaintOpen(false);
      setMaintEditing(null);
      setMaintForm(blankMaint);
      load();
    } catch {
      toast.error("Could not save schedule");
    }
  };

  const onMarkDone = async (id) => {
    await api.post(`/maintenance/${id}/mark-done`);
    toast.success("Marked done for today");
    load();
  };

  const onDeleteMaint = async (id) => {
    if (!window.confirm("Delete this maintenance schedule?")) return;
    await api.delete(`/maintenance/${id}`);
    toast.success("Schedule deleted");
    load();
  };

  const totalPurchaseCost = equipment.reduce((s, e) => s + (e.purchase_cost || 0), 0);
  const totalRepairs = repairs.reduce((s, r) => s + (r.cost || 0), 0);

  return (
    <div>
      <TopBar
        title="Equipment"
        subtitle="Your tractors, trucks, and implements — purchase cost, repairs, and receipts all in one place. Repairs auto-post to Expenses."
        actions={
          <button
            data-testid="add-equipment-btn"
            onClick={() => { setEqEditing(null); setEqForm({ ...blankEq, purchase_date: todayISO() }); setEqOpen(true); }}
            className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2 mt-6"
          >
            <Plus className="w-4 h-4" /> Add Equipment
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatTile label="Equipment Tracked" value={equipment.length} />
        <StatTile label="Total Purchase Cost" value={fmtUSD(totalPurchaseCost)} tint="green" />
        <StatTile label="Lifetime Repair Spend" value={fmtUSD(totalRepairs)} tint="red" />
      </div>

      {equipment.length === 0 ? (
        <EmptyState
          icon={Tractor}
          title="No equipment yet"
          hint="Track a tractor, combine, truck, or any implement. Purchases get auto-logged as equipment expenses."
          testid="empty-equipment"
        />
      ) : (
        <div className="space-y-4">
          {equipment.map((eq) => {
            const Icon = typeIcon(eq.type);
            const list = repairsFor(eq.id);
            const open = !!expanded[eq.id];
            const isDepreciate = eq.tax_treatment === "depreciate";
            const isMacrs = eq.depreciation_method === "macrs";
            const annualDep = isDepreciate && !isMacrs ? (eq.purchase_cost || 0) / (eq.useful_life_years || 7) : 0;
            return (
              <div key={eq.id} className="bg-white border border-[#1F1E1B]" data-testid={`equipment-card-${eq.id}`}>
                <div className="p-5 flex flex-wrap items-start gap-4">
                  <div className="w-12 h-12 bg-[#1F1E1B] flex items-center justify-center text-white shrink-0">
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="stamp">{eq.type}</span>
                      {isDepreciate ? (
                        <span className="stamp stamp-green">
                          {isMacrs ? `MACRS ${eq.useful_life_years}yr` : `Depreciate · ${eq.useful_life_years}yr`}
                        </span>
                      ) : (
                        <span className="stamp stamp-red">Expensed</span>
                      )}
                      {eq.receipts?.length > 0 && (
                        <ReceiptBadge receipts={eq.receipts} onClick={() => setGalleryReceipts(eq.receipts)} testid={`equipment-receipts-${eq.id}`} />
                      )}
                    </div>
                    <h3 className="font-display text-xl font-bold mt-2">{eq.name}</h3>
                    <div className="text-xs text-[#66645E] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Purchased {eq.purchase_date}</span>
                      {eq.serial_number && <span>S/N {eq.serial_number}</span>}
                      {isDepreciate && !isMacrs && <span>Annual depreciation: <span className="font-bold tabular">{fmtUSD(annualDep)}</span></span>}
                      {isDepreciate && isMacrs && <span>MACRS schedule ({eq.useful_life_years}-yr class)</span>}
                    </div>
                    {eq.notes && <div className="text-sm text-[#66645E] mt-2">{eq.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E]">Purchase</div>
                    <div className="font-display text-2xl font-black tabular">{fmtUSD(eq.purchase_cost)}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E] mt-2">Repairs total</div>
                    <div className="font-display text-xl font-black tabular text-[#BC4727]">{fmtUSD(totalRepairCost(eq.id))}</div>
                  </div>
                </div>

                <div className="border-t border-[#E5E2D6] px-4 sm:px-5 py-2.5 flex items-center justify-between bg-[#FAF9F4] flex-wrap gap-2">
                  <button
                    data-testid={`toggle-repairs-${eq.id}`}
                    onClick={() => setExpanded({ ...expanded, [eq.id]: !open })}
                    className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 text-[#1F1E1B]"
                  >
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Repairs ({list.length}) · Maint. ({maintFor(eq.id).length})
                  </button>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`add-maint-${eq.id}`}
                      onClick={() => openMaintModal(eq)}
                      className="bg-white border border-[#1F1E1B] text-[#1F1E1B] px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-[#1F1E1B] hover:text-white"
                    >
                      <CalendarClock className="w-3 h-3" /> Schedule
                    </button>
                    <button
                      data-testid={`add-repair-${eq.id}`}
                      onClick={() => openRepairModal(eq)}
                      className="bg-[#1F1E1B] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-[#2A2924]"
                    >
                      <Wrench className="w-3 h-3" /> Log Repair
                    </button>
                    <button data-testid={`edit-equipment-${eq.id}`} onClick={() => onEditEq(eq)} className="p-1 px-2 border border-[#1F1E1B] text-xs hover:bg-[#1F1E1B] hover:text-white">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button data-testid={`delete-equipment-${eq.id}`} onClick={() => onDeleteEq(eq.id)} className="p-1 px-2 border border-[#BC4727] text-[#BC4727] text-xs hover:bg-[#BC4727] hover:text-white">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-[#E5E2D6]" data-testid={`details-${eq.id}`}>
                    {/* Maintenance schedules */}
                    {maintFor(eq.id).length > 0 && (
                      <div className="px-4 sm:px-5 pt-4 pb-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">Maintenance schedules</div>
                        <div className="space-y-2">
                          {maintFor(eq.id).map((m) => (
                            <MaintRow key={m.id} m={m} onMarkDone={onMarkDone} onEdit={() => openMaintModal(eq, m)} onDelete={() => onDeleteMaint(m.id)} />
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Repairs list */}
                    <div className="overflow-x-auto" data-testid={`repairs-list-${eq.id}`}>
                    {list.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[#66645E]">No repairs logged yet.</div>
                    ) : (
                      <table className="ledger-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Vendor</th>
                            <th className="num">Cost</th>
                            <th>Receipts</th>
                            <th style={{ width: 90 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((r) => (
                            <tr key={r.id} data-testid={`repair-row-${r.id}`}>
                              <td className="tabular">{r.date}</td>
                              <td className="font-semibold">{r.description}</td>
                              <td className="text-[#66645E]">{r.vendor || "—"}</td>
                              <td className="num font-bold">{fmtUSD(r.cost)}</td>
                              <td>
                                <ReceiptBadge receipts={r.receipts} onClick={() => setGalleryReceipts(r.receipts)} testid={`repair-receipts-${r.id}`} />
                              </td>
                              <td>
                                <div className="flex gap-1.5 justify-end">
                                  <button data-testid={`edit-repair-${r.id}`} onClick={() => openRepairModal(eq, r)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                                  <button data-testid={`delete-repair-${r.id}`} onClick={() => onDeleteRepair(r.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Equipment modal */}
      {eqOpen && (
        <Modal title={eqEditing ? "Edit Equipment" : "Add Equipment"} onClose={() => setEqOpen(false)}>
          <form onSubmit={onSubmitEq} className="space-y-4" data-testid="equipment-form">
            <Field label="Name" required>
              <input data-testid="equipment-name-input" required value={eqForm.name} onChange={(e) => setEqForm({ ...eqForm, name: e.target.value })} placeholder="John Deere 8R 250" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Type">
                <AddableSelect
                  domain="equipment_type"
                  defaults={EQUIPMENT_TYPES}
                  value={eqForm.type}
                  onChange={(v) => setEqForm({ ...eqForm, type: v })}
                  testid="equipment-type-select"
                />
              </Field>
              <Field label="Serial Number">
                <input data-testid="equipment-serial-input" value={eqForm.serial_number} onChange={(e) => setEqForm({ ...eqForm, serial_number: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Purchase Date" required>
                <input data-testid="equipment-date-input" type="date" required value={eqForm.purchase_date} onChange={(e) => setEqForm({ ...eqForm, purchase_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Purchase Cost ($)" required>
                <input data-testid="equipment-cost-input" type="number" step="0.01" required value={eqForm.purchase_cost} onChange={(e) => setEqForm({ ...eqForm, purchase_cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <div className="border border-[#1F1E1B] p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">
                Tax Treatment
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    data-testid="tax-expense-radio"
                    checked={eqForm.tax_treatment === "expense"}
                    onChange={() => setEqForm({ ...eqForm, tax_treatment: "expense" })}
                    className="mt-0.5"
                  />
                  <span>
                    <strong>Expense now</strong> — full purchase price counts as an expense in {new Date(eqForm.purchase_date || todayISO()).getFullYear()}. Best for small tools and equipment under Section 179 limits.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    data-testid="tax-depreciate-radio"
                    checked={eqForm.tax_treatment === "depreciate"}
                    onChange={() => setEqForm({ ...eqForm, tax_treatment: "depreciate" })}
                    className="mt-0.5"
                  />
                  <span>
                    <strong>Depreciate</strong> — spread the cost over multiple tax years. Recommended for big iron.
                  </span>
                </label>
                {eqForm.tax_treatment === "depreciate" && (
                  <div className="ml-6 mt-2 space-y-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1">Method</div>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input
                            type="radio"
                            data-testid="method-straight-radio"
                            checked={eqForm.depreciation_method === "straight_line"}
                            onChange={() => setEqForm({ ...eqForm, depreciation_method: "straight_line" })}
                          />
                          Straight-line
                        </label>
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input
                            type="radio"
                            data-testid="method-macrs-radio"
                            checked={eqForm.depreciation_method === "macrs"}
                            onChange={() => setEqForm({ ...eqForm, depreciation_method: "macrs" })}
                          />
                          MACRS (IRS half-year)
                        </label>
                      </div>
                    </div>
                    {eqForm.depreciation_method === "macrs" ? (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1">Asset Class</div>
                        <select
                          data-testid="macrs-class-select"
                          value={eqForm.useful_life_years}
                          onChange={(e) => setEqForm({ ...eqForm, useful_life_years: parseInt(e.target.value) })}
                          className="w-full bg-white border border-[#1F1E1B] px-2 py-1.5 text-sm"
                        >
                          {MACRS_CLASSES.map((c) => (
                            <option key={c.life} value={c.life}>{c.label}</option>
                          ))}
                        </select>
                        <div className="text-[10px] text-[#66645E] mt-1">
                          Uses IRS Pub. 946 half-year convention percentage tables.
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="useful-life-input"
                          type="number"
                          min="2"
                          max="40"
                          value={eqForm.useful_life_years}
                          onChange={(e) => setEqForm({ ...eqForm, useful_life_years: e.target.value })}
                          className="w-20 bg-white border border-[#1F1E1B] px-2 py-1 text-sm tabular"
                        />
                        <span className="text-xs text-[#66645E]">useful life (years) · straight-line annual = cost ÷ life</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Field label="Notes">
              <textarea data-testid="equipment-notes-input" value={eqForm.notes} onChange={(e) => setEqForm({ ...eqForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <ReceiptUploader
              receipts={eqForm.receipts}
              onChange={(receipts) => setEqForm({ ...eqForm, receipts })}
              testidPrefix="equipment-receipt"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEqOpen(false)} data-testid="equipment-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="equipment-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Equipment</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Repair modal */}
      {repairOpen && (
        <Modal title={repairEditing ? "Edit Repair" : "Log Repair"} onClose={() => setRepairOpen(false)}>
          <form onSubmit={onSubmitRepair} className="space-y-4" data-testid="repair-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="repair-date-input" type="date" required value={repairForm.date} onChange={(e) => setRepairForm({ ...repairForm, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Cost ($)" required>
                <input data-testid="repair-cost-input" type="number" step="0.01" required value={repairForm.cost} onChange={(e) => setRepairForm({ ...repairForm, cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <Field label="Description" required>
              <input data-testid="repair-description-input" required value={repairForm.description} onChange={(e) => setRepairForm({ ...repairForm, description: e.target.value })} placeholder="e.g. Hydraulic line replacement" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Vendor">
                <input data-testid="repair-vendor-input" value={repairForm.vendor} onChange={(e) => setRepairForm({ ...repairForm, vendor: e.target.value })} placeholder="Deere Dealership" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Field (optional)">
                <select data-testid="repair-field-select" value={repairForm.field_id} onChange={(e) => setRepairForm({ ...repairForm, field_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="">— None —</option>
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="repair-notes-input" value={repairForm.notes} onChange={(e) => setRepairForm({ ...repairForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <ReceiptUploader
              receipts={repairForm.receipts}
              onChange={(receipts) => setRepairForm({ ...repairForm, receipts })}
              testidPrefix="repair-receipt"
            />
            <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
              <strong>Heads up:</strong> Saving will auto-create an Expense entry (category: Equipment) so this repair flows into your Schedule F.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setRepairOpen(false)} data-testid="repair-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="repair-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Repair</button>
            </div>
          </form>
        </Modal>
      )}

      {galleryReceipts && (
        <ReceiptGallery receipts={galleryReceipts} onClose={() => setGalleryReceipts(null)} />
      )}

      {maintOpen && (
        <Modal title={maintEditing ? "Edit Schedule" : "Maintenance Schedule"} onClose={() => setMaintOpen(false)}>
          <form onSubmit={onSubmitMaint} className="space-y-4" data-testid="maint-form">
            <Field label="Task" required>
              <input data-testid="maint-title-input" required value={maintForm.title} onChange={(e) => setMaintForm({ ...maintForm, title: e.target.value })} placeholder="Oil change, filter replacement, grease fittings…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Interval (days)" required>
                <input data-testid="maint-interval-input" type="number" min="1" required value={maintForm.interval_days} onChange={(e) => setMaintForm({ ...maintForm, interval_days: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Last Done" required>
                <input data-testid="maint-lastdone-input" type="date" required value={maintForm.last_done_date} onChange={(e) => setMaintForm({ ...maintForm, last_done_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="maint-notes-input" value={maintForm.notes} onChange={(e) => setMaintForm({ ...maintForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
              Next due will be calculated as <strong>last done + interval days</strong>. Use the "Mark done" button on the card to reset the clock.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setMaintOpen(false)} data-testid="maint-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="maint-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Schedule</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function MaintRow({ m, onMarkDone, onEdit, onDelete }) {
  const statusColor =
    m.status === "overdue" ? "bg-[#BC4727] text-white border-[#BC4727]"
    : m.status === "due_soon" ? "bg-[#E5A835] text-[#1F1E1B] border-[#E5A835]"
    : "bg-[#1A4135] text-white border-[#1A4135]";
  const Icon = m.status === "overdue" ? AlertTriangle : m.status === "due_soon" ? CalendarClock : CheckCircle2;
  const label = m.status === "overdue" ? `Overdue ${Math.abs(m.days_until_due)}d`
    : m.status === "due_soon" ? `Due in ${m.days_until_due}d`
    : `In ${m.days_until_due}d`;
  return (
    <div className="border border-[#D2CEC1] p-3 flex items-center gap-3 flex-wrap" data-testid={`maint-row-${m.id}`}>
      <div className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${statusColor}`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="flex-1 min-w-[140px]">
        <div className="font-semibold text-sm">{m.title}</div>
        <div className="text-xs text-[#66645E]">Every {m.interval_days}d · last done {m.last_done_date} · next {m.next_due_date}</div>
        {m.notes && <div className="text-xs text-[#66645E] mt-1 italic">{m.notes}</div>}
      </div>
      <div className="flex gap-1.5">
        <button data-testid={`maint-done-${m.id}`} onClick={() => onMarkDone(m.id)} className="border border-[#1A4135] text-[#1A4135] text-xs font-bold uppercase tracking-wider px-3 py-1 hover:bg-[#1A4135] hover:text-white flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Done today
        </button>
        <button data-testid={`edit-maint-${m.id}`} onClick={onEdit} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
        <button data-testid={`delete-maint-${m.id}`} onClick={onDelete} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

function StatTile({ label, value, tint }) {
  const bg = tint === "green" ? "bg-[#1A4135] border-[#1A4135] text-white" :
             tint === "red" ? "bg-[#BC4727] border-[#BC4727] text-white" :
             "bg-white border-[#1F1E1B]";
  const labelClr = tint ? (tint === "green" ? "text-[#C8D5CE]" : "text-[#F8D5C8]") : "text-[#66645E]";
  return (
    <div className={`p-4 border ${bg}`}>
      <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${labelClr}`}>{label}</div>
      <div className="font-display text-3xl font-black tabular">{value}</div>
    </div>
  );
}
